import React from 'react';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/solid';

interface QuestionAttempt {
  question_number: number;
  is_correct: boolean;
  difficulty_rating: number | null;
  selected_answer?: string;
  correct_answer?: string;
}

interface GameProgressPanelProps {
  attemptHistory: QuestionAttempt[];
  currentQuestionNumber: number;
  gameMode: 'survival' | 'rush';
  lives?: number;
  maxLives?: number;
  score?: number;
  timeRemaining?: number;
  totalTime?: number;
}

const GameProgressPanel: React.FC<GameProgressPanelProps> = ({
  attemptHistory,
  currentQuestionNumber,
  gameMode,
  lives,
  maxLives = 3,
  score,
  timeRemaining,
  totalTime,
}) => {
  const getDifficultyColor = (rating: number | null) => {
    if (rating === null || rating === undefined) return 'text-gray-500 dark:text-gray-400';
    if (rating <= 20) return 'text-green-600 dark:text-green-400';
    if (rating <= 30) return 'text-yellow-600 dark:text-yellow-400';
    if (rating <= 40) return 'text-orange-600 dark:text-orange-400';
    return 'text-red-600 dark:text-red-400';
  };

  const getDifficultyLabel = (rating: number | null) => {
    if (rating === null || rating === undefined) return 'Unknown';
    if (rating <= 20) return 'Easy';
    if (rating <= 30) return 'Medium';
    if (rating <= 40) return 'Hard';
    return 'Expert';
  };

  const formatTime = (milliseconds: number) => {
    const minutes = Math.floor(milliseconds / 60000);
    const seconds = Math.floor((milliseconds % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const getTimeColor = () => {
    if (!timeRemaining || !totalTime) return 'text-gray-600 dark:text-gray-400';
    const percentage = timeRemaining / totalTime;
    if (percentage > 0.5) return 'text-green-600 dark:text-green-400';
    if (percentage > 0.2) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-red-600 dark:text-red-400';
  };

  const accuracy = attemptHistory.length > 0
    ? (attemptHistory.filter(a => a.is_correct).length / attemptHistory.length * 100).toFixed(1)
    : '0.0';

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          Progress Tracker
        </h3>
        <div className="flex items-center space-x-4 text-sm">
          {gameMode === 'survival' && lives !== undefined && (
            <div className="flex items-center space-x-1">
              <span className="text-gray-600 dark:text-gray-400">Lives:</span>
              <span className="font-bold text-red-600 dark:text-red-400">
                {lives}/{maxLives}
              </span>
            </div>
          )}
          {gameMode === 'rush' && score !== undefined && (
            <div className="flex items-center space-x-1">
              <span className="text-gray-600 dark:text-gray-400">Score:</span>
              <span className="font-bold text-subject-600 dark:text-subject-400">
                {score}
              </span>
            </div>
          )}
          {timeRemaining !== undefined && (
            <div className="flex items-center space-x-1">
              <span className="text-gray-600 dark:text-gray-400">Time:</span>
              <span className={`font-bold ${getTimeColor()}`}>
                {formatTime(timeRemaining)}
              </span>
            </div>
          )}
          <div className="flex items-center space-x-1">
            <span className="text-gray-600 dark:text-gray-400">Accuracy:</span>
            <span className="font-bold text-blue-600 dark:text-blue-400">
              {accuracy}%
            </span>
          </div>
        </div>
      </div>

      {/* Question History Grid */}
      <div className="mb-4">
        <div className="grid grid-cols-10 sm:grid-cols-15 lg:grid-cols-20 gap-2">
          {Array.from({ length: Math.max(currentQuestionNumber, attemptHistory.length) }, (_, i) => {
            const questionNum = i + 1;
            const attempt = attemptHistory.find(a => a.question_number === questionNum);
            const isCurrent = questionNum === currentQuestionNumber && !attempt;

            return (
              <div
                key={questionNum}
                className={`relative flex flex-col items-center p-2 rounded-lg border transition-all ${
                  isCurrent
                    ? 'border-subject-500 bg-subject-50 dark:bg-subject-900/20'
                    : attempt
                    ? attempt.is_correct
                      ? 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20'
                      : 'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20'
                    : 'border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800'
                }`}
                title={
                  attempt
                    ? `Q${questionNum}: ${attempt.is_correct ? 'Correct' : 'Incorrect'} - Difficulty: ${getDifficultyLabel(attempt.difficulty_rating)}${attempt.difficulty_rating !== null && attempt.difficulty_rating !== undefined ? ` (${attempt.difficulty_rating})` : ''}`
                    : isCurrent
                    ? `Current Question ${questionNum}`
                    : `Question ${questionNum}`
                }
              >
                {/* Question Number */}
                <span className={`text-xs font-bold mb-1 ${
                  isCurrent
                    ? 'text-subject-700 dark:text-subject-300'
                    : attempt
                    ? attempt.is_correct
                      ? 'text-green-700 dark:text-green-300'
                      : 'text-red-700 dark:text-red-300'
                    : 'text-gray-500 dark:text-gray-400'
                }`}>
                  {questionNum}
                </span>

                {/* Icon */}
                <div className="mb-1">
                  {attempt ? (
                    attempt.is_correct ? (
                      <CheckCircleIcon className="h-4 w-4 text-green-600 dark:text-green-400" />
                    ) : (
                      <XCircleIcon className="h-4 w-4 text-red-600 dark:text-red-400" />
                    )
                  ) : isCurrent ? (
                    <div className="h-4 w-4 border-2 border-subject-500 rounded-full animate-pulse" />
                  ) : (
                    <div className="h-4 w-4 border border-gray-300 dark:border-gray-600 rounded-full" />
                  )}
                </div>

                {/* Difficulty Rating */}
                {attempt && (
                  <span className={`text-xs font-medium ${getDifficultyColor(attempt.difficulty_rating)}`}>
                    {attempt.difficulty_rating ?? 'N/A'}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center justify-center space-x-6 text-xs text-gray-600 dark:text-gray-400">
        <div className="flex items-center space-x-1">
          <CheckCircleIcon className="h-3 w-3 text-green-600 dark:text-green-400" />
          <span>Correct</span>
        </div>
        <div className="flex items-center space-x-1">
          <XCircleIcon className="h-3 w-3 text-red-600 dark:text-red-400" />
          <span>Incorrect</span>
        </div>
        <div className="flex items-center space-x-1">
          <div className="h-3 w-3 border-2 border-subject-500 rounded-full" />
          <span>Current</span>
        </div>
        <div className="flex items-center space-x-4 ml-4">
          <span className="text-green-600 dark:text-green-400">Easy (10-20)</span>
          <span className="text-yellow-600 dark:text-yellow-400">Medium (21-30)</span>
          <span className="text-orange-600 dark:text-orange-400">Hard (31-40)</span>
          <span className="text-red-600 dark:text-red-400">Expert (41-50)</span>
        </div>
      </div>
    </div>
  );
};

export default GameProgressPanel;
